import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { estagioHorarioService, estagioService } from '@/services/api';
import { ArrowLeftIcon, ClockIcon, CalendarIcon, UserIcon, BuildingOfficeIcon, PencilIcon, TrashIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

export const VerHorariosEstagioPage: React.FC = () => {
  const { estagioId } = useParams<{ estagioId: string }>();

  // Estados para controle de edição
  const [modoEdicao, setModoEdicao] = useState(false);
  const [horarioEditando, setHorarioEditando] = useState<number | null>(null);
  const [horariosEditados, setHorariosEditados] = useState<{ [key: number]: { horarioInicio: string, horarioFim: string } }>({});

  // Buscar dados do estágio
  const { data: estagioData, isLoading: estagioLoading } = useQuery({
    queryKey: ['estagio', estagioId],
    queryFn: () => estagioService.getById(parseInt(estagioId!)),
    enabled: !!estagioId,
  });

  // Buscar horários do estágio
  const { data: horariosData, isLoading: horariosLoading, refetch: refetchHorarios } = useQuery({
    queryKey: ['horarios-estagio', estagioId],
    queryFn: () => estagioHorarioService.getByEstagio(parseInt(estagioId!)),
    enabled: !!estagioId,
  });

  // Mutation para atualizar horário
  const updateHorarioMutation = useMutation({
    mutationFn: ({ id, data }: { id: number, data: any }) => estagioHorarioService.update(id, data),
    onSuccess: () => {
      toast.success('Horário atualizado com sucesso!');
      refetchHorarios();
      setHorarioEditando(null);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Erro ao atualizar horário';
      toast.error(errorMessage);
    },
  });

  // Mutation para deletar horário
  const deleteHorarioMutation = useMutation({
    mutationFn: (id: number) => estagioHorarioService.delete(id),
    onSuccess: () => {
      toast.success('Horário excluído com sucesso!');
      refetchHorarios();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Erro ao excluir horário';
      toast.error(errorMessage);
    },
  });

  const getDiaSemanaLabel = (dia: string) => {
    const dias = {
      'Segunda': 'Segunda-feira',
      'Terca': 'Terça-feira',
      'Quarta': 'Quarta-feira',
      'Quinta': 'Quinta-feira',
      'Sexta': 'Sexta-feira',
      'Sabado': 'Sábado',
      'Domingo': 'Domingo'
    };
    return dias[dia as keyof typeof dias] || dia;
  };

  const formatarHorario = (horario: string) => {
    // Se for um DateTime ISO, extrair apenas a parte do horário usando UTC
    if (horario.includes('T')) {
      const date = new Date(horario);
      return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    }
    return horario;
  };

  const extrairHoraMinuto = (horario: string) => {
    if (horario.includes('T')) {
      // Usar UTC para evitar problemas de fuso horário
      const date = new Date(horario);
      return `${date.getUTCHours().toString().padStart(2, '0')}:${date.getUTCMinutes().toString().padStart(2, '0')}`;
    }
    return horario;
  };

  const iniciarEdicaoHorario = (horario: any) => {
    setHorarioEditando(horario.id);
    const horarioInicio = extrairHoraMinuto(horario.horarioInicio);
    const horarioFim = extrairHoraMinuto(horario.horarioFim);

    setHorariosEditados({
      ...horariosEditados,
      [horario.id]: {
        horarioInicio,
        horarioFim
      }
    });
  };

  const cancelarEdicaoHorario = () => {
    setHorarioEditando(null);
    setHorariosEditados({});
  };

  const salvarHorario = (horario: any) => {
    const dadosEditados = horariosEditados[horario.id];

    if (dadosEditados) {
      const dataToSend = {
        estagioId: horario.estagioId,
        diaSemana: horario.diaSemana,
        horarioInicio: dadosEditados.horarioInicio,
        horarioFim: dadosEditados.horarioFim
      };

      updateHorarioMutation.mutate({
        id: horario.id,
        data: dataToSend
      });
    } else {
      toast.error('Dados de edição não encontrados');
    }
  };

  const atualizarHorarioEditado = (horarioId: number, campo: 'horarioInicio' | 'horarioFim', valor: string) => {
    setHorariosEditados(prev => ({
      ...prev,
      [horarioId]: {
        ...prev[horarioId],
        [campo]: valor
      }
    }));
  };

  const deletarHorario = (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este horário?')) {
      deleteHorarioMutation.mutate(id);
    }
  };

  // Agrupar horários por dia da semana
  const horariosPorDia = React.useMemo(() => {
    if (!horariosData) return {};

    return horariosData.reduce((acc: any, horario) => {
      const dia = horario.diaSemana;
      if (!acc[dia]) {
        acc[dia] = [];
      }
      acc[dia].push(horario);
      return acc;
    }, {});
  }, [horariosData]);

  const diasSemana = ['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'];

  if (estagioLoading || horariosLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Carregando dados...</p>
      </div>
    );
  }

  if (!estagioData) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Estágio não encontrado</p>
        <Button asChild className="mt-4">
          <Link to="/horarios">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Voltar
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/horarios">
              <ArrowLeftIcon className="h-4 w-4" />

            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Horários do Estágio
              {modoEdicao && <span className="ml-2 text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Modo Edição</span>}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {estagioData.aluno?.nome || 'Nome não encontrado'}
            </p>
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant={modoEdicao ? "default" : "outline"}
            size="sm"
            onClick={() => setModoEdicao(!modoEdicao)}
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            {modoEdicao ? 'Sair do Modo Edição' : 'Modo Edição'}
          </Button>
          <Button asChild>
            <Link to={`/horarios/novo?estagioId=${estagioId}`}>
              <PencilIcon className="h-4 w-4 mr-2" />
              Adicionar Horário
            </Link>
          </Button>
        </div>
      </div>

      {/* Informações do Estágio */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <UserIcon className="h-5 w-5 mr-2" />
            Informações do Estagiário
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <UserIcon className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-gray-500">Estagiário</p>
                <p className="text-lg font-semibold text-gray-900">
                  {estagioData.aluno?.nome || 'Nome não encontrado'}
                </p>
                <p className="text-sm text-gray-500">Estágio #{estagioData.id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <BuildingOfficeIcon className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm font-medium text-gray-500">Empresa</p>
                <p className="text-lg font-semibold text-gray-900">
                  {estagioData.empresa?.nome || 'Empresa não encontrada'}
                </p>
                <p className="text-sm text-gray-500">
                  {estagioData.empresa?.endereco || 'Endereço não informado'}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <CalendarIcon className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm font-medium text-gray-500">Período</p>
                <p className="text-lg font-semibold text-gray-900">
                  {new Date(estagioData.dataInicio).toLocaleDateString('pt-BR')} - {new Date(estagioData.dataTermino).toLocaleDateString('pt-BR')}
                </p>
                <p className="text-sm text-gray-500">
                  {estagioData.cargaHorariaSemanal}h/semana
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Horários por Dia da Semana */}
      {Object.keys(horariosPorDia).filter(dia => horariosPorDia[dia].length > 0).length === 0 ? (
        <Card>
          <CardContent className="text-center py-8">
            <ClockIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum horário cadastrado</h3>
            <p className="text-gray-500 mb-4">
              Este estágio ainda não possui horários de trabalho definidos.
            </p>
            <Button asChild>
              <Link to={`/horarios/novo?estagioId=${estagioId}`}>
                <PencilIcon className="h-4 w-4 mr-2" />
                Adicionar Primeiro Horário
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {diasSemana.map((dia) => {
            const horariosDoDia = horariosPorDia[dia] || [];

            // Só renderizar o card se houver horários para este dia
            if (horariosDoDia.length === 0) {
              return null;
            }

            return (
              <Card key={dia}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center">
                      <ClockIcon className="h-5 w-5 mr-2" />
                      {getDiaSemanaLabel(dia)}
                    </span>
                    <span className="text-sm font-normal text-gray-500">
                      {horariosDoDia.length} horário(s)
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {horariosDoDia.map((horario: any, index: number) => {
                      const estaEditando = horarioEditando === horario.id;
                      const dadosEditados = horariosEditados[horario.id];

                      return (
                        <div
                          key={horario.id}
                          className={`${estaEditando ? 'bg-yellow-50 border-yellow-300' : 'bg-blue-50 border-blue-200'} border rounded-lg p-4`}
                        >
                          {estaEditando ? (
                            // Modo de edição
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Horário Início
                                  </label>
                                  <input
                                    type="time"
                                    value={dadosEditados?.horarioInicio || ''}
                                    onChange={(e) => atualizarHorarioEditado(horario.id, 'horarioInicio', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Horário Fim
                                  </label>
                                  <input
                                    type="time"
                                    value={dadosEditados?.horarioFim || ''}
                                    onChange={(e) => atualizarHorarioEditado(horario.id, 'horarioFim', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  />
                                </div>
                              </div>
                              <div className="flex justify-end space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={cancelarEdicaoHorario}
                                  disabled={updateHorarioMutation.isPending}
                                >
                                  <XMarkIcon className="h-4 w-4 mr-1" />
                                  Cancelar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => salvarHorario(horario)}
                                  disabled={updateHorarioMutation.isPending}
                                >
                                  <CheckIcon className="h-4 w-4 mr-1" />
                                  Salvar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            // Modo de visualização
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-semibold text-blue-900">
                                  {formatarHorario(horario.horarioInicio)} - {formatarHorario(horario.horarioFim)}
                                </p>
                                <p className="text-sm text-blue-700">
                                  {index === 0 ? `Turno ${index + 1}` : `Turno ${index + 1}`}
                                </p>
                              </div>
                              <div className="flex items-center space-x-2">
                                {modoEdicao && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => iniciarEdicaoHorario(horario)}
                                      className="text-blue-600 hover:text-blue-700"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => deletarHorario(horario.id)}
                                      disabled={deleteHorarioMutation.isPending}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </>
                                )}
                                <p className="text-sm text-blue-600">
                                  ID: #{horario.id}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Resumo
      <Card>
        <CardHeader>
          <CardTitle>Resumo dos Horários</CardTitle>
          <CardDescription>
            Informações gerais sobre os horários cadastrados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{horariosData?.length || 0}</p>
              <p className="text-sm text-gray-500">Total de Horários</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">
                {Object.keys(horariosPorDia).filter(dia => horariosPorDia[dia].length > 0).length}
              </p>
              <p className="text-sm text-gray-500">Dias com Horários</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">
                {Math.max(...Object.values(horariosPorDia).map((horarios: any) => horarios.length), 0)}
              </p>
              <p className="text-sm text-gray-500">Máximo por Dia</p>
            </div>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
};
